import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { getUsageByRow, getUsageByCustomer } from '../../../../redux/services';
import { toast } from '../../../../redux/action';
import {
  Section,
  Title,
  Label,
  DateRangePicker,
  ExportButtons,
  ExportButton,
} from './UniversalRowsExport.styled';

/** Build CSV from array of objects and trigger download */
function downloadCSV(rows, filename) {
  if (!rows?.length) return;
  const keys = Object.keys(rows[0]);
  const header = keys.join(',');
  const escape = v =>
    v == null
      ? ''
      : String(v).includes(',')
      ? `"${String(v).replace(/"/g, '""')}"`
      : v;
  const lines = [
    header,
    ...rows.map(r => keys.map(k => escape(r[k])).join(',')),
  ];
  const blob = new Blob([lines.join('\n')], {
    type: 'text/csv;charset=utf-8;',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

const NO_DATA_MESSAGE =
  'No data available for the selected period. Try a different date range.';

const UniversalRowsExport = () => {
  const dispatch = useDispatch();
  const [dateRange, setDateRange] = useState(null);
  const [loading, setLoading] = useState(null);
  const [error, setError] = useState(null);

  const handleDateRangeChange = dates => {
    setDateRange(dates);
    setError(null);
  };

  const handleUsageByRow = async () => {
    const [start, end] = dateRange || [];
    setError(null);
    setLoading('row');
    try {
      const startDate = start ? start.toISOString().slice(0, 10) : undefined;
      const endDate = end ? end.toISOString().slice(0, 10) : undefined;
      const { results } = await getUsageByRow(startDate, endDate);
      if (!results?.length) {
        dispatch(toast('info', NO_DATA_MESSAGE, 4));
        return;
      }
      const dateLabel =
        startDate && endDate ? `${startDate}-to-${endDate}` : 'All Time';
      const filename = `Universal Rows Usage By Row ${dateLabel}.csv`;
      downloadCSV(results, filename);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Export failed');
    } finally {
      setLoading(null);
    }
  };

  const handleUsageByCustomer = async () => {
    const [start, end] = dateRange || [];
    setError(null);
    setLoading('customer');
    try {
      const startDate = start ? start.toISOString().slice(0, 10) : undefined;
      const endDate = end ? end.toISOString().slice(0, 10) : undefined;
      const { results } = await getUsageByCustomer(startDate, endDate);
      if (!results?.length) {
        dispatch(toast('info', NO_DATA_MESSAGE, 4));
        return;
      }
      const dateLabel =
        startDate && endDate ? `${startDate} to ${endDate}` : 'All Time';
      const filename = `Universal Rows Usage by Customer ${dateLabel}.csv`;
      downloadCSV(results, filename);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Export failed');
    } finally {
      setLoading(null);
    }
  };

  return (
    <Section>
      <Title>Universal Rows Usage Export</Title>

      <div>
        <Label>Select date range:</Label>
        <DateRangePicker
          value={dateRange}
          onChange={handleDateRangeChange}
          format="MMM DD, YYYY"
          allowClear
          placeholder={['Start date', 'End date']}
        />
      </div>

      <div>
        <Label>Available exports:</Label>
        <ExportButtons>
          <ExportButton
            type="button"
            onClick={handleUsageByRow}
            disabled={loading != null}
          >
            {loading === 'row' ? 'Exporting…' : 'Usage by Row'}
          </ExportButton>
          <ExportButton
            type="button"
            onClick={handleUsageByCustomer}
            disabled={loading != null}
          >
            {loading === 'customer' ? 'Exporting…' : 'Usage by Customer'}
          </ExportButton>
        </ExportButtons>
      </div>
      {error && (
        <p style={{ color: '#c00', fontSize: 14, margin: 0 }}>{error}</p>
      )}
    </Section>
  );
};

export default UniversalRowsExport;
